import { Logger } from '@/utils/logger';
import { HttpStatus } from '@montugroup/apis-styleguide';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { CodeInputDialog } from '@montugroup/circuit-shared/components/common/CodeInputDialog';
import { useMFAController } from '@montugroup/circuit-shared/hooks/auth/useMFAController';
import getQueryClient from '@montugroup/circuit-shared/utils/getQueryClient';
import { Alert, Link, LoadingButton, Stack, toast, Typography } from '@montugroup/design-system';
import { isErrorResponse, TsRestResponseError } from '@ts-rest/core';
import { isNotKnownResponseError } from '@ts-rest/react-query/v5';
import { useCallback, useState } from 'react';
import ProfileFormLoadingSkeleton from './ProfileFormLoadingSkeleton';

const logger = new Logger('MFAEnrolment');
const client = getTsApiV1Client();
const queryClient = getQueryClient();

export function MFAEnrolment() {
  const [isMFAPending, setIsMFAPending] = useState(false);
  const { data, isLoading, contractEndpoint, error } = client.mfa.listMfaMethods.useQuery({
    queryKey: client.mfa.listMfaMethods.keys,
  });
  const enableMfaMutation = client.mfa.enableMfaMethod.useMutation();
  const disableMfaMutation = client.mfa.disableMfaMethod.useMutation();
  const mfaController = useMFAController();

  // NOTE: We only support sms at this stage, so we are hardcoding enrolment check to that method type
  const isEnrolled = data?.body.data.sms?.active;
  const relevantMutation = isEnrolled ? disableMfaMutation : enableMfaMutation;

  const handleSuccess = useCallback(() => {
    const successMessage = `Successfully ${isEnrolled ? 'unenrolled from' : 'enrolled in'} the MFA Service`;
    toast.success(successMessage);
    logger.info(successMessage);
    queryClient.invalidateQueries({ queryKey: client.mfa.listMfaMethods.keys });
  }, [isEnrolled]);

  const toggleMfa = useCallback(async () => {
    try {
      setIsMFAPending(true);

      const request = async (challenge?: { id: string; code: string }) =>
        relevantMutation.mutateAsync({
          params: { type: 'sms' },
          body: {
            data: { ...(challenge ? { challenge } : {}) },
          },
        });

      const response = await request();

      if (response.status === HttpStatus.NO_CONTENT) {
        return handleSuccess();
      } else if (response.status === HttpStatus.ACCEPTED) {
        return mfaController
          .issueNewChallenge({
            challengeId: response.body.data.challenge.id,
            maskedDestination: response.body.data.challenge.channel.destination,
            requestCode: async () => {
              const resendCodeResponse = await request();

              if (resendCodeResponse.status === HttpStatus.ACCEPTED) {
                return resendCodeResponse.body.data.challenge.id;
              }

              // TODO: handle if MFA is disabled while the user is on this dialog (resending the code will return a 200 response and should be handled gracefully)
              throw new TsRestResponseError(relevantMutation.contractEndpoint, resendCodeResponse);
            },
            validateCode: async (challenge) => {
              const challengeAttemptResponse = await request({
                id: challenge.id || response.body.data.challenge.id,
                code: challenge.code,
              });

              if (challengeAttemptResponse.status === HttpStatus.NO_CONTENT) {
                return handleSuccess();
              }

              throw new TsRestResponseError(relevantMutation.contractEndpoint, challengeAttemptResponse);
            },
          })
          .finally(() => setIsMFAPending(false));
      }

      throw new TsRestResponseError(relevantMutation.contractEndpoint, response);
    } catch (error) {
      // TODO: handle specific errors better here
      toast.error('Something went wrong, please try again');
      logger.error('toggleMfa', error);
    }
  }, [mfaController, relevantMutation]);

  const MFAEnrolmentTitle = () => {
    return (
      <Typography variant="subtitle1" component="h2" sx={(theme) => ({ color: theme.palette.primary.main })}>
        {isEnrolled ? 'Disable' : 'Enable'} Multi-Factor Authentication (MFA)
      </Typography>
    );
  };
  const MFAEnrolmentDescription = () => {
    return (
      <Typography variant="body2" sx={(theme) => ({ color: theme.palette.primary.main })}>
        {isEnrolled
          ? 'Turn off MFA for your account. This will remove the additional SMS verification step when logging in. Not recommended for security reasons.'
          : "Add an extra layer of security to your account by enabling MFA. You'll receive a unique code via SMS to verify your identity when logging in."}
      </Typography>
    );
  };

  if (isNotKnownResponseError(error, contractEndpoint)) {
    logger.error('isNotKnownResponseError', error);

    return (
      <Stack gap={6}>
        <MFAEnrolmentTitle />
        <Alert severity="error">Something went wrong loading the MFA Service</Alert>
      </Stack>
    );
  }

  // TODO: handle known error cases with helpful messages
  if (isErrorResponse(error, contractEndpoint)) {
    logger.error('isErrorResponse', error);

    return (
      <Stack gap={6}>
        <MFAEnrolmentTitle />
        <Alert severity="error">Something went wrong loading the MFA Service</Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack>
        <MFAEnrolmentTitle />
        <ProfileFormLoadingSkeleton />
      </Stack>
    );
  }

  return (
    <Stack gap={6} alignItems={'flex-start'}>
      <MFAEnrolmentTitle />
      <MFAEnrolmentDescription />

      <Link
        variant="body2"
        color="secondary.main"
        href="https://support.alternaleaf.com.au/hc/en-au/articles/14441372555407-Multi-factor-Authentication"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more about our MFA service
      </Link>

      <LoadingButton
        loading={isMFAPending}
        variant={isEnrolled ? 'outlined' : 'contained'}
        color="primary"
        size="large"
        sx={{
          width: {
            xs: '100%',
            md: 'auto',
          },
        }}
        onClick={() => toggleMfa()}
      >
        {isEnrolled ? 'Unenrol from MFA' : 'Enrol now'}
      </LoadingButton>
      <CodeInputDialog controller={mfaController} />
    </Stack>
  );
}
